//eslint-disable @typescript-eslint/no-explicit-any
import * as Yup from 'yup';
import dayjs from 'dayjs';

// Types for configuration
export interface DateValidationOptions {
  allowPast?: boolean;
  allowToday?: boolean;
  allowFuture?: boolean;
  requiredMessage?: string;
  pastDateMessage?: string;
  futureDateMessage?: string;
  todayNotAllowedMessage?: string;
  invalidDateMessage?: string;
  checkTime?: boolean;
  pastTimeMessage?: string;
}

// Default messages
const DEFAULT_MESSAGES = {
  required: 'Date is required',
  invalidDate: 'Date must be a valid date',
  pastDate: 'Date cannot be in the past',
  futureDate: 'Future dates are not allowed',
  todayNotAllowed: "Today's date is not allowed",
  pastTime: 'Time cannot be in the past for today\'s date'
};

/**
 * Core date transformation function
 * Handles dayjs objects, Date objects, and string dates
 */
const transformDate = (value: any, originalValue: any) => {
  if (dayjs.isDayjs(originalValue)) {
    return originalValue.toDate();
  }
  if (originalValue instanceof Date) {
    return originalValue;
  }
  if (typeof originalValue === 'string' && originalValue) {
    return dayjs(originalValue).toDate();
  }
  return null;
};

/**
 * Global date validation function with full customization
 * @param options Configuration options for date validation
 * @returns Yup validation schema
 */
export const createDateValidation = (options: DateValidationOptions = {}) => {
  const {
    allowPast = false,
    allowToday = true,
    allowFuture = true,
    requiredMessage = DEFAULT_MESSAGES.required,
    pastDateMessage = DEFAULT_MESSAGES.pastDate,
    futureDateMessage = DEFAULT_MESSAGES.futureDate,
    todayNotAllowedMessage = DEFAULT_MESSAGES.todayNotAllowed,
    invalidDateMessage = DEFAULT_MESSAGES.invalidDate,
    checkTime = false,
    pastTimeMessage = DEFAULT_MESSAGES.pastTime
  } = options;

  return Yup.mixed()
    .transform(transformDate)
    .typeError(invalidDateMessage)
    .required(requiredMessage)
    .test('date-validation', function(value) {
      if (!value) return false;
      
      const now = dayjs();
      const selectedDateTime = dayjs(value);
      const today = dayjs().startOf('day');
      const selectedDate = dayjs(value).startOf('day');
      
      // Past date validation
      if (selectedDate.isBefore(today) && !allowPast) {
        return this.createError({ message: pastDateMessage });
      }
      
      // Today validation
      if (selectedDate.isSame(today)) {
        if (!allowToday) {
          return this.createError({ message: todayNotAllowedMessage });
        }
        
        // Time validation for today
        if (checkTime && selectedDateTime.isBefore(now)) {
          return this.createError({ message: pastTimeMessage });
        }
      }
      
      // Future date validation
      if (selectedDate.isAfter(today) && !allowFuture) {
        return this.createError({ message: futureDateMessage });
      }
      
      return true;
    });
};

/**
 * Quick preset validation functions for common use cases
 */

// 1. Standard date validation (today + future allowed)
export const dateValidation = (requiredMessage?: string, options: DateValidationOptions = {}) => 
  createDateValidation({
    allowPast: false,
    allowToday: true,
    allowFuture: true,
    ...options,
    requiredMessage
  });

// 2. Future dates only (excluding today)
export const futureDateValidation = (requiredMessage?: string) => 
  createDateValidation({
    allowPast: false,
    allowToday: false,
    allowFuture: true,
    requiredMessage,
    pastDateMessage: 'Please select a future date'
  });

// 3. Any date allowed (for historical records)
export const anyDateValidation = (requiredMessage?: string) => 
  createDateValidation({
    allowPast: true,
    allowToday: true,
    allowFuture: true,
    requiredMessage
  });

// 4. Past dates only (for completed events)
export const pastDateValidation = (requiredMessage?: string) => 
  createDateValidation({
    allowPast: true,
    allowToday: true,
    allowFuture: false,
    requiredMessage,
    futureDateMessage: 'Future dates are not allowed'
  });

// 5. Date with time validation
export const dateTimeValidation = (requiredMessage?: string) => 
  createDateValidation({
    allowPast: false,
    allowToday: true,
    allowFuture: true,
    checkTime: true,
    requiredMessage
  });

// 6. Business days only (Monday to Friday, future dates)
export const businessDateValidation = (requiredMessage?: string) => {
  return createDateValidation({
    allowPast: false,
    allowToday: true,
    allowFuture: true,
    requiredMessage
  }).test('business-day', 'Please select a business day (Monday to Friday)', function(value) {
    if (!value) return false;
    
    const dayOfWeek = dayjs(value).day();
    return dayOfWeek >= 1 && dayOfWeek <= 5; // Monday = 1, Friday = 5
  });
};

// 7. Date range validation (between two dates)
export const dateRangeValidation = (
  minDate: dayjs.Dayjs | Date | string,
  maxDate: dayjs.Dayjs | Date | string,
  requiredMessage?: string
) => {
  return Yup.mixed()
    .transform(transformDate)
    .typeError(DEFAULT_MESSAGES.invalidDate)
    .required(requiredMessage || DEFAULT_MESSAGES.required)
    .test('date-range', function(value) {
      if (!value) return false;
      
      const selected = dayjs(value);
      const min = dayjs(minDate);
      const max = dayjs(maxDate);
      
      if (selected.isBefore(min)) {
        return this.createError({
          message: `Date must be after ${min.format('MMM DD, YYYY')}`
        });
      }
      
      if (selected.isAfter(max)) {
        return this.createError({
          message: `Date must be before ${max.format('MMM DD, YYYY')}`
        });
      }
      
      return true;
    });
};

/**
 * Utility function to create custom validation messages
 */
export const createCustomMessages = (overrides: Partial<typeof DEFAULT_MESSAGES>) => ({
  ...DEFAULT_MESSAGES,
  ...overrides
});

/**
 * Helper function for conditional date validation
 * Example: Date required only if another field has a specific value
 */
export const conditionalDateValidation = (
  condition: (formValues: any) => boolean,
  validationSchema: any,
) => {
  return Yup.mixed().when('$root', {
    is: condition,
    then: validationSchema,
    otherwise: Yup.mixed().notRequired()
  });
};

// Export commonly used validation presets
export const COMMON_DATE_VALIDATIONS = {
  // Rental/Order dates
  rental: (msg?: string) => dateValidation(msg || 'Rental date is required'),

};

/**
 * Usage Examples:
 * 
 * // Basic usage with custom required message
 * date: dateValidation('Please select a valid date')
 * 
 * // Full customization
 * date: createDateValidation({
 *   allowPast: false,
 *   allowToday: true,
 *   allowFuture: true,
 *   requiredMessage: 'Date is required',
 *   pastDateMessage: 'Cannot select past dates'
 * })
 * 
 * // Preset validations
 * rentalDate: COMMON_DATE_VALIDATIONS.rental('Rental date is required')
 * bookingDate: COMMON_DATE_VALIDATIONS.booking()
 * 
 * // Date range
 * eventDate: dateRangeValidation(
 *   dayjs().add(1, 'day'),
 *   dayjs().add(1, 'year'),
 *   'Event date is required'
 * )
 */